import { sideOf, type PasurDealResult, type PasurMove, type PasurView } from "@bing/engine-pasur";
import { useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { MatchEndCard } from "../components/MatchEndCard";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import { PasurTable } from "../screens/PasurTable";
import {
  getBotDifficulty,
  getCardDeck,
  getPasurTarget,
  setBotDifficulty,
  setCardDeck,
  setPasurTarget,
} from "../settings/prefs";
import type { BotDifficulty, CardDeckId, PasurTarget } from "@bing/shared";
import { SegmentedChoice } from "../components/SegmentedChoice";
import { SoloStartScreen } from "../components/SoloStartScreen";
import { StartRow } from "../components/StartScreenShell";
import { takeResumeIntent } from "./fresh-solo";
import { useSession } from "../store/context";
import { useOverlayActive } from "./lifecycle";
import { useOutcomeSfx } from "./game-sfx";
import { END_REVEAL_DELAY_MS, useEndReveal } from "./use-end-reveal";
import { finalDealOf } from "./pasur-breakdown";
import { PASUR_END_MARGIN_MS } from "./pasur-moments";
import type { GameResult } from "./connection";
import { faDigits } from "../i18n/digits";
import { pasurSeatFallback, pasurSideWords } from "./seat-labels";
import { presetAvatarFor } from "./seat-avatar";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatIdentity,
  type SeatPlayer,
} from "./seat-identity";
import { createColyseusGameConnector } from "./game-connection";
import { createGameStore } from "./game-store";
import { useTableChannelGate } from "./table-channel-gate";
import { useSoloLoad } from "./use-solo-load";
import { CardDeckContext } from "./card-decks";
import { useDeckGate } from "./use-card-faces";
import { handKeyOf, useHandDeck } from "./hand-deck";
import { PASUR_TARGET_CHOICES } from "./match-targets";

/**
 * A FRAME TODAY'S TABLE CAN DRAW (final review A-2, I3). Prod's game-server plays master's پاسور
 * engine, whose frames carry `sweeps` and none of the match's fields; the table reads
 * `totalScores[yourSide]` on its first render and `lastDeal` on every later frame, so such a frame
 * crashed the WHOLE app (the root ErrorBoundary, a `render-crash` report each time) — anyone resuming
 * an old solo save between the web push and the game-server's, or on a game-server-only rollback.
 * Both wrappers meet it with the maintenance notice instead (true in a push window); the game-server
 * push then retires such a save with its own notice. It asks the fields a master frame lacks and
 * the table reads at once; `dealer`, `finalRound` and `reshuffles` are read too, unasked — a master
 * frame lacks them as well, so one absent field is enough to tell.
 */
export function isCurrentPasurView(view: unknown): view is PasurView {
  const v = view as Partial<Record<keyof PasurView, unknown>> | null | undefined;
  return (
    !!v &&
    Array.isArray(v.totalScores) &&
    Array.isArray(v.tallies) &&
    Array.isArray(v.surs) &&
    Array.isArray(v.buried) &&
    typeof v.yourSide === "number" &&
    typeof v.dealNumber === "number" &&
    typeof v.targetScore === "number" &&
    v.lastDeal !== undefined
  );
}

/** The design's win overlay: dimmed table, trophy, final scores, and play-again /
 * exit. پاسور is TWO SIDES playing a match to the target, so the card groups the seats by side —
 * «ما» and «حریف», each with its ONE match total and its players by face and name — the winners'
 * side first and MARKED («برنده»), and no per-seat rank (partners share a total; ①–④ over four
 * seats was false). Under them, the deal that ended the match, each side's points (P1 Task 7, W18);
 * over them, WHY it ended when the ROOM ended it — `GameResult.reason` read the دوز way
 * (`DoozEndOverlay`), a line per side. Never a draw (D16): a پاسور match ends only on a UNIQUE top
 * total, so `tied` is never read. Since card-98 item 12 the shared template (`MatchEndCard`) draws it,
 * in the form its room holds (the template's measured forms replaced Task 8b's six-form ladder).
 * Exported so the online Pasur game reuses the exact same overlay. */
export function WinOverlay({
  result,
  totalScores,
  targetScore,
  you,
  self,
  players,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  finalDeal = null,
  onLeave,
}: {
  result: GameResult;
  /**
   * Per SIDE, the match totals — read off the VIEW, never off `result.seatScores`: a match the
   * ROOM ends (a side gone, «opponents-left») is scored 1 per winning seat and 0 per losing one,
   * and printing that as a total would be a wrong number on the card (card-91's condition).
   */
  totalScores: readonly number[];
  /** The match's target — said once under the heading. */
  targetScore: number;
  you: number;
  /** The signed-in user's identity — their own row wears their real photo + name. */
  self?: SeatIdentity | null;
  /**
   * The lobby's known seat occupants. WITHOUT IT THIS CARD CANNOT TELL A PERSON FROM A BOT: it
   * falls back to a positional label and a stand-in face, so four real people were announced
   * «بازیکن 1 · بازیکن 2 · …» while the felt behind the card named every one of them. The same
   * defect بی‌دل shipped, in the same shape. Undefined in solo (and before the first lobby
   * frame), which is exactly when the positional label is the right answer.
   */
  players?: readonly SeatPlayer[];
  onAgain: () => void;
  /** ONLINE, private tables only: «بازی دوباره» is the HOST's call, so a guest sees the
   *  button inert until the host has offered one. Undefined (solo, public) ⇒ enabled. */
  againDisabled?: boolean;
  /** Why it is inert, in one Persian line. Rendered only when present. */
  againHint?: string;
  /** CONTRACT C3 — the rematch cannot work from here: NO «بازی دوباره» at all (a dead button
   *  that looks alive is worse than none); `againHint` may still say why. Undefined (solo, and
   *  wherever the button is shown) ⇒ drawn. */
  againHidden?: boolean;
  /**
   * The deal that ENDED the match (`finalDealOf(view)`): its points per side under the rows. Null —
   * the default, today's callers' — when there is none to show: a match the ROOM ended mid-deal
   * carries an OLDER deal, and an older deal's numbers would read as the last one's.
   */
  finalDeal?: PasurDealResult | null;
  onLeave: () => void;
}): ReactNode {
  const won = result.winners.includes(you);
  /*
   * WHY IT ENDED, when the ROOM ended it — the دوز way, a line per side, each true for whoever reads
   * it (the away-copy rule): «opponents-left», the one reason a partnership table sends (a side
   * whose two chairs are both bots' now — a leave, a close, a drop past its grace, an idle removal
   * all end there: `endIfTeamAbandoned`), and «idle», a table for two's idle removal. An ENGINE
   * terminal carries no reason: the totals say it.
   */
  const roomReason =
    result.reason === "opponents-left"
      ? won
        ? fa.pasur.endOpponentsLeft
        : fa.pasur.endYourSideLeft
      : result.reason === "idle"
        ? won
          ? fa.pasur.endOpponentIdle
          : fa.pasur.endYouIdle
        : // «خروج» at a table for two (the two-seat forfeit): the winner is told; the leaver is gone
          result.reason === "resign" && won
          ? fa.pasur.endOpponentResigned
          : null;
  // The SAME two resolvers the felt uses, with the SAME fallback — so a chair cannot be a named
  // person on the table and «بازیکن N» on the result card.
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, you, self, players, {
      you: fa.pasur.you,
      fallback: pasurSeatFallback,
    });
  const avatarOf = (seat: number) => resolveSeatAvatar(seat, you, self, players, presetAvatarFor);
  // One block per SIDE, no rank glyphs: the winners' side first — which a match the ROOM ended can
  // hand to the side with FEWER points, so the order follows `winners`, never the totals.
  const mine = sideOf(you);
  const seats = Array.from({ length: result.seatScores.length }, (_u, seat) => seat);
  // at a table for TWO the viewer is one player: «شما» / «حریف», and the heading the match's result
  const words = pasurSideWords(seats.length);
  const sides = [0, 1]
    .map((side) => ({
      side,
      total: totalScores[side] ?? 0,
      seats: seats.filter((seat) => sideOf(seat) === side),
      won: result.winners.some((seat) => sideOf(seat) === side),
    }))
    .sort((a, b) => Number(b.won) - Number(a.won));
  // card-98 item 12: the shared template — the winning feel, a row a SIDE (its faces and names, as شلم
  // draws a team), پاسور's own «برنده» mark and match totals; its own lines: why the room ended it,
  // else the result's heading and the target; the deal that ended the match under the rows
  return (
    <MatchEndCard
      label={fa.pasur.gameOver}
      outcome={won ? "won" : "lost"}
      glyph="🎴"
      title={won ? fa.pasur.youWon : fa.pasur.youLost}
      reason={
        <>
          {/* why the ROOM ended it (its own line, as before), then the result's heading and the target */}
          {roomReason !== null ? <div data-testid="pasur-end-reason">{roomReason}</div> : null}
          <div>{`${seats.length === 2 ? fa.pasur.matchResult : fa.pasur.scores} · ${fa.pasur.target(targetScore)}`}</div>
        </>
      }
      sides={sides.map((row) => ({
        key: `side-${row.side}`,
        mine: row.side === mine,
        mark: row.won ? (
          <span
            data-winner-mark=""
            style={{
              fontSize: 11,
              fontWeight: 800,
              lineHeight: 1.5,
              padding: "0 8px",
              borderRadius: 999,
              background: "var(--accent)",
              color: "var(--accent-text)",
              whiteSpace: "nowrap",
            }}
          >
            {fa.pasur.winner}
          </span>
        ) : undefined,
        // card-a7 B8 (the operator): every player by face and name WHOLE on a row of their own
        perMember: true,
        members: row.seats.map((seat) => ({ seat, name: nameOf(seat), avatar: avatarOf(seat) })),
        result: faDigits(row.total),
        attrs: { "data-side": row.side === mine ? "us" : "them" },
      }))}
      lead={
        finalDeal ? (
          // The deal that ended the match: each side's points, «ما» the viewer's side, each figure its
          // own ltr isolate, held to its side's word by a NO-BREAK space (final review B-2)
          <div data-testid="pasur-final-deal" style={{ fontSize: 13, color: "var(--hint)" }}>
            {`${fa.pasur.finalDeal}: ${words.us}\u00a0`}
            <span dir="ltr" style={{ fontWeight: 800 }}>{`+${faDigits(
              finalDeal.points[mine] ?? 0,
            )}`}</span>
            {` · ${words.them}\u00a0`}
            <span dir="ltr" style={{ fontWeight: 800 }}>{`+${faDigits(
              finalDeal.points[1 - mine] ?? 0,
            )}`}</span>
          </div>
        ) : null
      }
      promo
      // card-a7 B8 (the operator: every screen works on his phone): read without a scroll — the
      // template steps down while its body scrolls at all, to its «dense» form (card-98's kit fix)
      fitWhole
      playAgain={fa.pasur.playAgain}
      leave={fa.pasur.leave}
      onAgain={onAgain}
      againDisabled={againDisabled}
      againHint={againHint}
      againHidden={againHidden}
      onLeave={onLeave}
    />
  );
}

/** Owns the Pasur connection + store, and renders connect/error/table/game-over. */
function PasurSoloRound({
  difficulty,
  target,
  onAgain,
}: {
  /** FIXED for this game — chosen on the start screen, never changes mid-game. */
  difficulty: BotDifficulty;
  /** The match target, chosen with it. The server ignores it on a resume (the save carries its own). */
  target: PasurTarget;
  onAgain: () => void;
}): ReactNode {
  const setView = useSession((s) => s.setView);
  const initData = useSession((s) => s.initDataRaw);
  // YOUR seat wears your real photo and name — on the felt and on the end card, as online.
  const profile = useSession((s) => s.profile);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [round, setRound] = useState(0);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endFailed, setEndFailed] = useState(false);

  const store = useMemo(
    () =>
      createGameStore(
        createColyseusGameConnector<PasurMove, PasurView>({
          wsUrl: GAME_SERVER_URL,
          room: "pasur",
          getInitData: () => initData,
          // FIXED for this game — chosen on the start screen, never changes mid-game.
          difficulty,
          pasurTarget: target,
        }),
      ),
    // `round` bumps to start a fresh game (new connection) on "play again".
    [initData, round, difficulty, target],
  );

  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  // Hold the summary overlay until the table has DRAINED — the match's last deal end (its last
  // capture, the scoop), the deal's card for its whole span, and whatever the terminal frame brought
  // ahead of them — and PASUR_END_MARGIN_MS more (Task 8a, residual 4: never a fixed time from the
  // frame, which a backlog outlasts). A result whose frame is no terminal keeps the shell's beat.
  const [holding, setHolding] = useState(false);
  const showEnd = useEndReveal(
    result !== null && !holding,
    view?.phase === "gameOver" ? PASUR_END_MARGIN_MS : END_REVEAL_DELAY_MS,
  );
  // The win/lose chime lands WITH the summary (it used to fire ~1.4s early).
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  // The app-root reconnect overlay owns the screen while it's up — don't ALSO
  // paint this container's raw-error screen behind it (that leaked English).
  const overlayActive = useOverlayActive();
  // A NEW game the game-server refused for channel membership: the channel-join screen, whose
  // «عضو شدم» re-attempts the join (`round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      setView("lobby");
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  // The classic SVG faces are a separate chunk (see `game/card-faces.ts`): the table may not
  // be revealed until they are in, or it would deal blank cards. The bar's floor is a MINIMUM,
  // not a timeout — on a slow connection it simply waits longer.
  const deck = useContext(CardDeckContext);
  const faces = useDeckGate(deck);
  const { loading, progress, waitingForAssets } = useSoloLoad(
    view != null,
    ready,
    store,
    faces.ready,
  );
  // the deck this table DRAWS: latched per hand from the felt's first card — no hand while the
  // loading screen is up (`hand-deck.ts`, N1) — a pick lands at the next deal, never mid-hand
  const drawDeck = useHandDeck(deck, loading ? null : handKeyOf("pasur", view));

  // «خروج» — leave but KEEP the game resumable (a plain disconnect no longer ends it).
  const doLeave = (): void => {
    store.getState().disconnect();
    setView("lobby");
  };
  // «پایان بازی» — END the game server-side and WAIT for the server's ack (the
  // ActiveGame row is provably deleted) BEFORE dropping it locally and leaving. On
  // failure/timeout stay in the game, keep it listed as in-progress (it IS), and
  // surface a retry dialog — never navigate-and-forget an unconfirmed end.
  const doEnd = (): void => {
    if (ending) return;
    setEnding(true);
    setEndFailed(false);
    void store
      .getState()
      .endGame()
      .then(() => {
        clearActiveGame("pasur"); // off the resume strip right away; Lobby refetch confirms
        doLeave();
      })
      .catch(() => setEndFailed(true))
      .finally(() => setEnding(false));
  };
  // The in-game exit is gated behind the two-option confirm dialog first.
  const leave = (): void => setConfirmingLeave(true);
  // «بازی دوباره» returns to the START SCREEN rather than re-dealing at the same settings —
  // the same flow حکم/شطرنج/منچ/تخته‌نرد use, so the next game can be set up before it deals.
  const again = (): void => {
    store.getState().disconnect();
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? fa.pasur.connectionError}
          </p>
          <Button variant="primary" onClick={() => setView("lobby")}>
            {fa.pasur.leave}
          </Button>
        </div>
      </div>
    );
  }

  if (loading || !view) {
    // Retries are spent and the artwork still is not here: an explicit, recoverable screen —
    // never a silent stall, and never a table dealt with blank cards.
    if (faces.failed) {
      return <Boot error={fa.status.offline} onRetry={faces.retry} onLeave={doLeave} />;
    }
    return (
      <Boot
        caption={waitingForAssets ? fa.status.preparingCards : fa.pasur.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={fa.pasur.leave}
      />
    );
  }
  // A frame the table cannot draw — the OLD game-server's (`isCurrentPasurView`): the maintenance
  // notice and the way out, the save kept (final review A-2, I3)
  if (!isCurrentPasurView(view))
    return <Boot error={fa.errors.MAINTENANCE} onLeave={doLeave} leaveLabel={fa.pasur.leave} />;

  return (
    <CardDeckContext.Provider value={drawDeck}>
      {/* The header's exit stays reachable beside the end card (the card starts below the band);
          once the game is over it is the card's «خروج» — there is no game left to keep or end. */}
      <PasurTable
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : leave}
        self={profile}
        onHolding={setHolding}
      />
      {result && showEnd ? (
        // Already over (ActiveGame cleared by the terminal finish) → plain exit.
        <WinOverlay
          result={result}
          totalScores={view.totalScores}
          targetScore={view.targetScore}
          you={view.you}
          self={profile}
          finalDeal={finalDealOf(view)}
          onAgain={again}
          onLeave={doLeave}
        />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            doEnd();
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
      {endFailed ? (
        // The server never ACKed the end — the game is still in progress and still
        // on the resume list. Same shared dialog, as the error surface: retry or stay.
        <ConfirmDialog
          title={fa.confirm.endFailedTitle}
          body={fa.confirm.endFailedBody}
          confirmLabel={fa.confirm.endRetry}
          onConfirm={doEnd}
          onCancel={() => setEndFailed(false)}
        />
      ) : null}
    </CardDeckContext.Provider>
  );
}

/**
 * Owns the START SCREEN → game flow. The match target, the opponent level and the card design are
 * chosen here, before the deal, and stay FIXED for the game; «بازی دوباره» comes back to ask again.
 *
 * THE TARGET is the private table's own control, with the same list (`SegmentedChoice`, `PASUR_TARGET_CHOICES`):
 * ۶۲ on first use, then REMEMBERED PER DEVICE (`bing:pasurTarget`), as شلم's is. The server validates
 * the pick as a private host's and ignores it on a resume.
 *
 * A RESUME stands the screen aside ({@link takeResumeIntent}): the saved game already carries
 * the settings it began with, and the lobby arms that signal for every solo resume.
 */
export function PasurGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  const [phase, setPhase] = useState<"select" | "playing">(() =>
    takeResumeIntent() ? "playing" : "select",
  );
  const [target, setTarget] = useState<PasurTarget>(getPasurTarget());
  const [difficulty, setDifficulty] = useState<BotDifficulty>(getBotDifficulty());
  const [deck, setDeck] = useState<CardDeckId>(getCardDeck());

  if (phase === "select") {
    return (
      <SoloStartScreen
        title={fa.pasur.title}
        emoji="🎣"
        heading={fa.soloStart.heading}
        leaveLabel={fa.pasur.leave}
        difficulty={difficulty}
        onDifficultyChange={(v) => {
          setDifficulty(v);
          setBotDifficulty(v);
        }}
        deck={deck}
        onDeckChange={(v) => {
          setDeck(v);
          setCardDeck(v);
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      >
        {/* the rules of the match first (`SoloStartScreen`): how many points win it */}
        <StartRow label={fa.pasur.chooseTarget}>
          <SegmentedChoice
            label={fa.pasur.chooseTarget}
            options={PASUR_TARGET_CHOICES}
            value={target}
            onChange={(v) => {
              setTarget(v);
              setPasurTarget(v);
            }}
            compact
          />
        </StartRow>
      </SoloStartScreen>
    );
  }
  // The DECK CHOSEN ON THE START SCREEN, not the one App read when it rendered: App computes
  // `getCardDeck()` once, above this component, and never re-renders on a phase flip — so the
  // pick would not reach the table without this nested provider (the inner one wins).
  return (
    <CardDeckContext.Provider value={deck}>
      <PasurSoloRound difficulty={difficulty} target={target} onAgain={() => setPhase("select")} />
    </CardDeckContext.Provider>
  );
}
